import { CoreTextView } from '@core/widgets';
import { useTranslation } from '@libs/core';
import React from 'react';
import { TextStyle } from '../types/styles';
import { ButtonRowView } from '../widgets/Buttons/ButtonRowView';
import { SeparatorView } from '../widgets/Separator/SeparatorView';
import { PopupComponent } from '../widgets/UI/Popup/PopupComponent';
import { ARROW_RETRY_ICON } from './Offline';

/**
 * - `offline` — the device has no connection. Recovery means retrying the failed request.
 * - `slowService` — the device is online (confirmed by the connectivity probe) but our backend has not
 *   answered yet. The request is still in flight, so the only safe action is to keep waiting: replaying
 *   it could submit the same action twice, and reloading would abandon an operation the server may have
 *   already accepted.
 */
export type TOfflinePopupVariant = 'offline' | 'slowService';

type TOfflinePopupProps = {
    /** Called by the action button. For `slowService` this only dismisses the popup. */
    onRetry?: () => void;
    variant: TOfflinePopupVariant;
};

const VARIANTS: Record<TOfflinePopupVariant, { headerKey: string; messageKey: string; buttonKey: string }> = {
    offline: {
        headerKey: 'bs.internet.connection.restored.header',
        messageKey: 'bs.internet.connection.restored.message',
        buttonKey: 'bs.internet.connection.restored.button.label',
    },
    slowService: {
        headerKey: 'bs.ps.unavailable.header',
        messageKey: 'bs.ps.unavailable.message',
        buttonKey: 'bs.internet.connection.restored.button.label',
    },
};

const OfflinePopup = ({ onRetry, variant = 'offline' }: TOfflinePopupProps) => {
    const { t } = useTranslation();
    const { headerKey, messageKey, buttonKey } = VARIANTS[variant];

    return (
        <PopupComponent
            withoutSeparator
            internalContent={
                <>
                    <CoreTextView
                        text={t(headerKey)}
                        styleProps={{ ...TextStyle.main, textAlign: 'center', margin: { right: 24, left: 24, bottom: 16, top: 32 } }}
                    />
                    <CoreTextView
                        text={t(messageKey)}
                        styleProps={{
                            ...TextStyle.main,
                            main: 'var(--grey_72_lc)',
                            textAlign: 'center',
                            margin: { right: 24, left: 24, bottom: 64, top: 16 },
                        }}
                    />
                    <SeparatorView />
                    <ButtonRowView key={'try-again'} onClick={onRetry} label={t(buttonKey)} iconName={ARROW_RETRY_ICON} margin={{ top: 16 }} />
                </>
            }
        />
    );
};

export default OfflinePopup;
